import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AssociationsService } from 'app/services/associations.service';
import { AssociationHelper } from 'app/storage/association.storage';
import { catchError, EMPTY, fromEventPattern, lastValueFrom } from 'rxjs';
import { Alert } from 'app/shared/alerts/simple-message/simple-message.component';
import { MeetingFormService } from '../meeting-form.service';
import { SessionStorage } from 'app/storage/session.storage';
import { Epe } from 'app/models/epe';
import { SopStorage } from 'app/storage/sop.storage';
import { Project } from 'app/models/project';
import { TOPIC_GROUPS } from 'app/models/epe-topics';
import { jsPDF } from 'jspdf';
import { collectFieldErrors, FieldError, focusAndScrollToField } from 'app/shared/forms/form-validation.util';

@Component({
  selector: 'report',
  templateUrl: './report.component.html',
  styleUrls: ['../meeting-form.component.scss', '../form-steps/form-steps.scss', './report.component.scss']
})
export class ReportComponent implements OnInit {

  form_structure_name = "service_report"
  isPss: boolean = false
  form: FormGroup
  loading: boolean = true

  project?: Project
  topics: string = ''
  questions: Question[];
  title: string;

  showAlert: boolean = false
  alertMessage: string = ''
  alertType: Alert = Alert.SUCCESS

  toSubmit: boolean = true
  summary: {[index:string]:any} = {}
  read_only: boolean = false
  opened: boolean = false
  @ViewChild('summaryBox', {static: false}) summaryBox!: ElementRef

  submitted: boolean = false
  fieldErrors: FieldError[] = []

  constructor(
    private fb: FormBuilder,
    private formService: MeetingFormService,
    private mongoService: AssociationsService,
    private hostRef: ElementRef,
    private helper: AssociationHelper,
    private router: Router,
    private meetingService: MeetingFormService,
    private auth: SessionStorage,
    private cache: SopStorage,
  ) { 
    //this.formService.clear() 
    this.isPss = this.helper.isPss()
    if(this.isPss)
      this.form_structure_name = "pss_report"

    try{
      let data = require(`assets/meeting-form/${this.form_structure_name}.json`)
      this.questions = data['questions']
      this.title = data['title']
    } catch (e){
      this.questions = []
      this.title = ''
    }

    const requiredArray = () => new FormArray([], [Validators.required, Validators.minLength(1)])
    this.form = this.isPss ? this.buildPssForm() : this.fb.group({
      "1) Instructions to fill in this report": [''],
      "2.1) Project description": ['', Validators.required],
      "2.2) Description of expectations and meetings": ['', Validators.required],
      "3.1) Standardisation readiness": ['', Validators.required],
      "3.2) Summary of the Standardisation Orientation Tool advice": ['', Validators.required],
      "3.2.1) Standards and standardisation mapping landscape": requiredArray(),
      "3.2.2) What results have already been achieved or are expected to be achieved in the next few months by the project based on the delivered activities?": ['', Validators.required],
      "3.3) Access to standards": requiredArray(),
      "3.4) Standardisation strategy and engagement": requiredArray(),
      "3.5) Standards deliverables": requiredArray(),
      "3.6) Training material": requiredArray(),
      "3.6.1) Did the service recipient complete any of the following (tick what appropriate)?": requiredArray(),
      "3.6.2) If applicable, please specify any other training material suggested to the project in the box below": ['', Validators.required],
      "4) Assessment of standardisation potential": [''],
      "4.1) Indicator 1: Necessity": requiredArray(),
      "4.2) Indicator 2: Transferability": requiredArray(),
      "4.3) Indicator 3: Feasibility": requiredArray(),
      "4.4) Indicator 4: Complementation of standardization landscape": requiredArray(),
      "4.5) Indicator 5: Further input needed": requiredArray(),
      "5) Final Recommendations": [''],
      "5.1) What is the recommended strategy and next steps for the project?": ['', Validators.required],
      "5.2) Would you recommend that the project pursues the accelerator service?": ['', Validators.required],
      "6) Closure": [''],
      "6.1) Has the project completed the satisfaction survey?": requiredArray(),
      "6.2) Has the project booked a slot for follow up interview?": requiredArray(),
    })
    this.formService.setReportForm(this.form)
  }

  // PSS report: controls built from pss_report.json (headers "" get no control)
  private buildPssForm(): FormGroup {
    const group = this.fb.group({})
    for (const question of this.questions) {
      const key = this.getQuestionKey(question)
      const value = this.getQuestion(question)
      const required = this.PSS_REQUIRED_KEYS.has(key)
      if (Array.isArray(value)) {
        group.addControl(key, new FormArray([], required ? [Validators.required, Validators.minLength(1)] : []))
        // tick-box only adds the "- Other" control once rendered; create it upfront so a saved
        // value survives the read-only summary and saves made before the panel is opened
        if (value.some(answer => /\(please specify\)/i.test(answer)))
          group.addControl(`${key} - Other`, new FormControl(''))
      }
      else if (value)
        group.addControl(key, new FormControl('', required ? Validators.required : null))
    }
    return group
  }


  getTopicTags(key: any = null, key2: any = null): any {
    const topics = this.project?.topics
    if (!topics) return []

    // New flow: topics is flat string[] of values
    if (Array.isArray(topics)) {
      if (key || key2) return []
      return this.resolveTopicLabels(topics as unknown as string[])
    }

    // Legacy nested object structure
    if (key2) {
      const tmp: any = topics[key]
      return tmp ? Object.values(tmp[key2]) : null
    }
    if (key) return Object.keys(topics[key])
    return Object.keys(topics)
  }

  private resolveTopicLabels(values: string[]): string[] {
    const labels: string[] = []
    for (const value of values) {
      let found = false
      for (const group of TOPIC_GROUPS) {
        for (const sub of group.subGroups) {
          if (sub.value === value) { labels.push(sub.label); found = true; break }
          for (const child of sub.children) {
            if (child.value === value) { labels.push(child.label); found = true; break }
          }
          if (found) break
        }
        if (found) break
      }
      if (!found) labels.push(value)
    }
    return labels
  }

  private readonly REQUIRED_KEYS = new Set<string>([
    "2.1) Project description",
    "2.2) Description of expectations and meetings",
    "3.1) Standardisation readiness",
    "3.2) Summary of the Standardisation Orientation Tool advice",
    "3.2.1) Standards and standardisation mapping landscape",
    "3.2.2) What results have already been achieved or are expected to be achieved in the next few months by the project based on the delivered activities?",
    "3.3) Access to standards",
    "3.4) Standardisation strategy and engagement",
    "3.5) Standards deliverables",
    "3.6) Training material",
    "3.6.1) Did the service recipient complete any of the following (tick what appropriate)?",
    "3.6.2) If applicable, please specify any other training material suggested to the project in the box below",
    "4.1) Indicator 1: Necessity",
    "4.2) Indicator 2: Transferability",
    "4.3) Indicator 3: Feasibility",
    "4.4) Indicator 4: Complementation of standardization landscape",
    "4.5) Indicator 5: Further input needed",
    "5.1) What is the recommended strategy and next steps for the project?",
    "5.2) Would you recommend that the project pursues the accelerator service?",
    "6.1) Has the project completed the satisfaction survey?",
    "6.2) Has the project booked a slot for follow up interview?",
  ])

  private readonly PSS_REQUIRED_KEYS = new Set<string>([
    "1.1) Summarise the call objective, expected outcomes, relevant standardisation or regulatory wording, and why these matter to the proposal.",
    "1.2) Describe why the proposal lead requested the service, the questions they wanted answered, and the results they expected.",
    "2.1.1) Proposal-stage standardisation maturity",
    "2.1.2) Recommended ambition level",
    "2.1.3) Rationale",
    "2.2) Key opportunities and constraints",
    "3.1) Tick all recommendation categories covered in your advice",
    "3.2) Detailed recommendations",
  ])

  private get requiredKeys(): Set<string> {
    return this.isPss ? this.PSS_REQUIRED_KEYS : this.REQUIRED_KEYS
  }

  isFieldRequired(key: string): boolean {
    return this.requiredKeys.has(key)
  }

  getTopicTag(key: any) {
    const result: any = this.project?.topics ? (this.project.topics as any)[key] : null
    return result
  }

  async ngOnInit() {
    if(!this.helper.getSelectedProject() || !this.helper.getSelectedEpe()){
      this.loading = false
      return
    }
    this.project = this.cache.getProjects()?.find((p: any) => p.application_id == this.helper.getSelectedProject()) ?? new Project()
    let response = await lastValueFrom(this.mongoService.getReportForm(this.helper.getSelectedProject(), this.helper.getSelectedEpe()), {defaultValue: null})
    let form: { [key: string]: any; } | null = response ? response['report'] : null
    if(!form){
      if(this.auth.isProject()){
        this.read_only = true
      }
      this.loading = false
      return
    }
    this.formService.updateReportForm(form)

    this.loading = false
    
    if(this.router.url == "/form/(meeting-form:meeting_5/(meeting_5:summary))"){
      this.toSubmit = false
    }

    this.summary = this.meetingService.getReportSubmit()

    if(this.auth.isProject() || this.helper.getMeetingForm() > 5){
      this.read_only = true
    }
    else{
      this.read_only = false
    }
  }

  isOpenQuestion(question: Question) {
    return typeof question[this.getQuestionKey(question)] === 'string'
  }

  getQuestionKey(question: Question): string{
    return Object.keys(question)[0]
  }

  getQuestion(question: Question): string[] | string{
    return Object.values(question)[0]
  }

  getQuestionDescription(question: Question): string[] | string{
    return question["description"] ? question["description"] : ""
  }

  getKeys(): any[] {
    //console.log(this.summary)
    return Object.keys(this.summary)
  }

  confirm(): void{
    this.submitted = true
    this.fieldErrors = collectFieldErrors(
      this.form,
      Array.from(this.requiredKeys).map(key => ({ key, label: key }))
    )
    if(this.fieldErrors.length){
      focusAndScrollToField(this.hostRef.nativeElement, this.fieldErrors[0].key)
      return
    }

    this.mongoService.submitReportForm(
      this.helper.getSelectedProject(),
      this.helper.getSelectedEpe(),
      this.formService.getReportSubmit()
      )
      .pipe(
        catchError(
          err => {
            this.addAlert("Activity Report update failed! Please contact the administrator", Alert.ERROR)
            return EMPTY
          }
        )
      )
      .subscribe(
        () => {
          this.addAlert("Activity Report succesfully updated", Alert.SUCCESS)
        }
      )
  }

  jumpToField(key: string): void {
    focusAndScrollToField(this.hostRef.nativeElement, key)
  }


  addAlert(message: string, type: Alert){
    this.alertMessage = message
    this.alertType = type
    this.showAlert = true
  }

  async exportReportSummary() {
    const pdf = new jsPDF();
    const logoPng = await this.svgToPng('assets/img/hsbooster.svg', 300, 100);
    if (logoPng) pdf.addImage(logoPng, 'PNG', 10, 10, 50, 18);

    const x = 10;
    const textBoxWidth = 190;
    const pageHeight = pdf.internal.pageSize.getHeight();
    let y = 38;

    // Title
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(20);
    pdf.setTextColor(0, 78, 154);
    pdf.text(this.isPss ? "PSS Activity Report" : "Activity Report", x, y);
    pdf.setTextColor(0, 0, 0);
    y += 10;

    // Project info block
    const p: any = this.project || {};
    const acronym = p.project_acronym || '—';
    const fullName = p.full_project_name || '—';
    const owner = `${p.name || ''} ${p.surname || ''}`.trim() || '—';
    const org = p.organisation_type || '—';
    const email = p.email || '—';
    const today = new Date().toISOString().split('T')[0];

    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Project information', x, y);
    y += 6;

    pdf.setFontSize(9);
    const infoLines: [string, string][] = [
      ['Project acronym', acronym],
      ['Full project name', fullName],
      ['Project representative', owner],
      ['Organisation type', org],
      ['Email', email],
      ['Report date', today],
    ];
    for (const [label, value] of infoLines) {
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${label}:`, x, y);
      pdf.setFont('helvetica', 'normal');
      const valueLines = pdf.splitTextToSize(String(value), textBoxWidth - 50);
      pdf.text(valueLines, x + 50, y);
      y += 6 * valueLines.length;
    }

    y += 4;
    pdf.setDrawColor(0, 78, 154);
    pdf.setLineWidth(0.4);
    pdf.line(x, y, x + textBoxWidth, y);
    y += 8;

    // Report sections
    pdf.setFontSize(10);
    for (const key of Object.keys(this.summary)) {
      if (y > pageHeight - 25) { pdf.addPage(); y = 20; }
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 78, 154);
      const keyLines = pdf.splitTextToSize(key, textBoxWidth);
      pdf.text(keyLines, x, y);
      y += 6 * keyLines.length;

      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'normal');
      const value = this.summary[key]
      const valueText = value === true ? '✓' : this.stripHtmlTags(value)
      const lines = pdf.splitTextToSize(valueText, textBoxWidth);
      for (const line of lines) {
        if (y > pageHeight - 20) { pdf.addPage(); y = 20; }
        pdf.text(line, x, y);
        y += 6;
      }
      y += 4;
    }

    const fileSlug = this.fileSlug(acronym, p.surname || p.name);
    pdf.save(`${this.isPss ? 'pss_' : ''}activity_report_${fileSlug}.pdf`);
  }

  private svgToPng(url: string, width: number, height: number): Promise<string | null> {
    return new Promise(resolve => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas')
          canvas.width = width
          canvas.height = height
          const ctx = canvas.getContext('2d')
          if (!ctx) return resolve(null)
          ctx.clearRect(0, 0, width, height)
          ctx.drawImage(img, 0, 0, width, height)
          resolve(canvas.toDataURL('image/png'))
        } catch { resolve(null) }
      }
      img.onerror = () => resolve(null)
      img.src = url
    })
  }

  private fileSlug(...parts: string[]): string {
    const slug = parts.filter(Boolean).join('_').toLowerCase()
      .replace(/[^a-z0-9_]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '')
    return slug || 'report'
  }

  stripHtmlTags(content: string): string {
    const div = document.createElement('div');
    div.innerHTML = content;
    return div.textContent || div.innerText || '';
  }
  


}

export interface Question {
  [key:string]: string[] | string
}
